import React, { useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import axiosInstance from '../api/axios';
import { resolve403ErrorMessage } from '../request/getApiErrorMessage.ts';
import SystemPickerByUuid from './SystemPickerByUuid';
import { useAuditYear } from '../contexts/AuditYearContext.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';

const INPUT_CLASSES =
  'w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';

const SECTION_LABEL_CLASSES =
  'block text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3';

const FIELD_LABEL_CLASSES = 'block text-sm font-medium text-gray-700 mb-1';

const HINT_CLASSES = 'text-xs text-gray-500 mt-1.5';

function ReportCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedAuditYearId, selectedAuditYear } = useAuditYear();
  const isLocked = Boolean(selectedAuditYear?.is_locked);

  // When ReportCreate is reached via the "+ Add new report" button on the
  // system edit page, location.state.fromSystem carries the originating
  // system's id + return path so we can: (a) auto-link the new report to
  // that system, (b) seed the Name field with whatever the user typed in
  // the system-page search, and (c) navigate back to the system after save
  // instead of dumping the user on /sox-reports.
  const fromSystem = location.state?.fromSystem ?? null;

  // No reportId state — the backend's trg_reports_set_report_id trigger
  // assigns the per-org RPT_NNNNNN counter on insert. We send an empty
  // string in the POST body to opt into the trigger every time.
  const [name, setName] = useState(fromSystem?.prefilledName || '');
  const [isReport, setIsReport] = useState(false);
  const [keyReport, setKeyReport] = useState(false);
  const [standard, setStandard] = useState(true);
  const [systemIds, setSystemIds] = useState(
    fromSystem?.systemId ? [fromSystem.systemId] : []
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = useMemo(
    () => name.trim().length > 0 && name.trim().length <= 255 && !submitting && !isLocked,
    [name, submitting, isLocked]
  );

  const handleSave = async () => {
    if (!canSubmit) return;
    const trimmedName = name.trim();
    if (trimmedName.length > 255) {
      setError('Name must be 255 characters or fewer');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axiosInstance.post('/api/reports', {
        // Empty string lets the backend trigger fill it (RPT_NNNNNN per-org).
        reportId: '',
        name: trimmedName,
        isReport,
        keyReport,
        standard,
        systemIds,
        auditYearId: selectedAuditYearId || undefined,
      });
      // Round-trip back to the system page that sent us here (if any) so the
      // user sees the new report attached without losing their place.
      navigate(fromSystem?.returnPath || '/sox-reports');
    } catch (err) {
      console.error('Create report failed:', err);
      setError(resolve403ErrorMessage(err, err?.response?.data?.error || 'Failed to create report'));
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate(fromSystem?.returnPath || '/sox-reports');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ReadOnlyBanner />

        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create document</h1>
            <p className="text-sm text-gray-600 mt-1 max-w-xl">
              Add a document to the catalog and optionally mark it as a report. Map it to the systems that produce it.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
          >
            Cancel
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-6 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Summary card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className={SECTION_LABEL_CLASSES}>Summary</h2>

          <div className="mb-4">
            <label htmlFor="r-name" className={FIELD_LABEL_CLASSES}>
              Name <span className="text-red-600">*</span>
            </label>
            <input
              id="r-name"
              type="text"
              placeholder="e.g. Quarterly Balance Sheet"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={INPUT_CLASSES}
            />
            {name.length > 255 ? (
              <p className="text-xs text-red-600 mt-1.5">Name must be 255 characters or fewer</p>
            ) : null}
          </div>

          <div>
            <label htmlFor="r-code" className={FIELD_LABEL_CLASSES}>
              Document ID <span className="text-xs font-normal text-gray-400 ml-1">(auto-generated)</span>
            </label>
            {/* System-assigned identifier — the backend's
                trg_reports_set_report_id trigger fills it with the next
                RPT_NNNNNN per-org counter on insert. We render the field
                read-only so the user can't accidentally type into it
                and assume their value was kept. The actual value lands
                back on the report list page after save. */}
            <input
              id="r-code"
              type="text"
              readOnly
              disabled
              placeholder="Auto-generated on save (DOC_000001 …)"
              value=""
              className={`${INPUT_CLASSES} font-mono bg-gray-100 text-gray-500 cursor-not-allowed`}
              aria-describedby="r-code-hint"
            />
            <p id="r-code-hint" className={HINT_CLASSES}>
              The platform assigns the document ID on save. Used as the stable code in matrices and
              working papers.
            </p>
          </div>
        </div>

        {/* Classification card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className={SECTION_LABEL_CLASSES}>Classification</h2>

          <div className="mb-5">
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={isReport}
                onChange={(e) => setIsReport(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Consider this document as report</span>
            </label>
          </div>

          <div className="mb-5">
            <label className={FIELD_LABEL_CLASSES}>Scope</label>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="report-key"
                  checked={keyReport}
                  onChange={() => setKeyReport(true)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                Key
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="report-key"
                  checked={!keyReport}
                  onChange={() => setKeyReport(false)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                Non-Key
              </label>
            </div>
          </div>

          <div>
            <label className={FIELD_LABEL_CLASSES}>Type</label>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="report-standard"
                  checked={standard}
                  onChange={() => setStandard(true)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                Standard
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="report-standard"
                  checked={!standard}
                  onChange={() => setStandard(false)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                Custom
              </label>
            </div>
          </div>
        </div>

        {/* Linked systems card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className={SECTION_LABEL_CLASSES}>Linked systems</h2>
          <p className="text-sm text-gray-600 mb-3">
            Pick the systems that produce or feed this report or document. Saved as a many-to-many mapping in system reports.
          </p>
          <SystemPickerByUuid value={systemIds} onChange={setSystemIds} />
        </div>

        {/* Footer actions */}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSubmit}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-300 disabled:border-blue-300 disabled:cursor-not-allowed"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Saving…' : 'Save document'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReportCreate;
